import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { AxiosError } from 'axios';
import { parse } from 'qs';

import { useCapyValues } from '@/hooks/useCapyValues';
import { useTurnstile } from '@/hooks/useTurnstile';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import i18n from '@/settings/i18n';
import { sentryReportError } from '@/utils/sentryReportError';
import { pages } from '@/settings/pages';
import { adminApi } from '@/services/api';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';

import { ActivationStart, Values } from '../Presentation';
import { stringify } from 'query-string';

export const ActivationStartContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();

  // 送信中フラグ（二重送信防止）
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Capy関連（Capy廃止後に削除）
  const [capyLoading, setCapyLoading] = useState(true);
  const [capyError, setCapyError] = useState(false);
  const { setCapyValues, createParamsWithCapyValues } = useCapyValues();

  // Turnstile関連
  const {
    isLoading: turnstileLoading,
    turnstileError,
    onSuccess: onTurnstileSuccess,
    onError: onTurnstileError,
    resetError: resetTurnstileError,
    createParamsWithTurnstile,
  } = useTurnstile();

  /**
   * Capyパズル完了時のハンドラ
   * - setCapyValuesが呼ばれる = パズル完了
   * - パズル完了を検知して送信ボタンを活性化する
   */
  const handleCapyValuesChange: typeof setCapyValues = useCallback(
    (values) => {
      setCapyLoading(false);
      setCapyValues(values);
    },
    [setCapyValues],
  );

  /**
   * Feature Flag取得（Turnstile/Capy切り替え）
   * - useTurnstile: Turnstile有効（true=Turnstile、false=Capy）
   * - loading: Feature Flag取得中かどうか（true=Spinner表示、false=CAPTCHA表示）
   */
  const { enabled: useTurnstileFlag, loading: featureFlagLoading } =
    useFeatureFlag('activation_turnstile_enabled');

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = parse(location.search, { ignoreQueryPrefix: true }) as {
    email?: string;
    locale?: string;
  };
  const initialEmail = searchParams.email || '';

  useEffect(() => {
    const { locale } = searchParams;
    if (locale) {
      i18n.changeLanguage(locale);
      delete searchParams.locale;
      navigate(
        {
          pathname: location.pathname,
          search: stringify(searchParams),
        },
        { replace: true },
      );
    }
  }, [location, navigate, searchParams]);

  const onSubmit = useCallback(
    async (values: Values) => {
      try {
        setIsSubmitting(true);
        setCapyError(false);
        resetTurnstileError();

        let data;

        if (useTurnstileFlag) {
          // Turnstileモード
          const result = createParamsWithTurnstile(values);
          if (!result.isValid) {
            setIsSubmitting(false);
            return;
          }
          data = result.data;
        } else {
          // Capyモード
          data = createParamsWithCapyValues(values);
        }

        await adminApi.activateAccountMail({ data });
        navigate(pages.token.sent);
      } catch (err) {
        if (err instanceof AxiosError && err.response !== undefined) {
          // 送信ボタン再活性化（再送信可能にする）
          setIsSubmitting(false);

          if (err.response && err.response.data.error === 'already_activated') {
            navigate(pages.activation.alreadyCompleted);
            return;
          }

          // Capyエラー（Capy廃止後に削除）
          if (!useTurnstileFlag && err.response.status === 401) {
            setCapyError(true);
            return;
          }

          // 4xx, 5xx → 予期せぬエラーメッセージを表示
          sentryReportError(err);
          flashError(i18n.t('ui.error.unexpected'));
        }
      }
    },
    [
      useTurnstileFlag,
      createParamsWithTurnstile,
      createParamsWithCapyValues,
      resetTurnstileError,
      flashError,
      navigate,
    ],
  );

  // 送信ボタン無効化条件
  // - 送信中は常に無効化
  // - Capyモードの場合はパズル未完了なら無効化
  // - Turnstileモードの場合は送信中のみ無効化（Turnstileのローディング状態は FormContents で判定）
  const isSubmitButtonDisabled = useTurnstileFlag
    ? isSubmitting
    : capyLoading || isSubmitting;

  const childProps = {
    initialEmail,
    isDisabled: isSubmitButtonDisabled,
    capyError,
    onSubmit,
    setCapyValues: handleCapyValuesChange,
    useTurnstile: useTurnstileFlag,
    turnstileError,
    turnstileLoading, // FormContents で ignoreTurnstile との組み合わせ判定に使用
    onTurnstileSuccess,
    onTurnstileError,
    featureFlagLoading,
  };
  return <ActivationStart {...childProps} />;
};
